import { useState } from 'react'
import { useSelector } from 'react-redux'
import { canViewFollowUpComparison } from 'services/results/follow-up-access'
import { StrokedButton } from 'views/components/UI/Buttons'
import SkillsComparisonDialog from 'views/components/FollowUp/SkillsComparisonDialog'
import CardTypeTest from 'views/components/Cards/TestType'
import skillIcon from 'assets/images/icon-skills-card.png'
import { formatMeasurementDate, formatMeasurementLabel } from 'utils/measurement-label'
import {
  BetesaMeasurementCard,
  BetesaMeasurementLabel,
  BetesaGroup,
  BetesaFollowUpList,
  MeasurementMeta,
} from './styled'

export interface SkillsMeasurement {
  user_test_id: number
  test_id: number
  process_id: number
  category_name: string
  completed_test: boolean
  is_follow_up?: boolean
  root_user_test_id?: number | null
  previous_user_test_id?: number | null
  language?: string
  send_date?: string | null
  answer_date?: string | null
  betesa_test_id?: number | null
  domination?: string | null
}

// Use recorded ancestry, never merely the category or the array order.
export function skillsMeasurementGroups(measurements: SkillsMeasurement[], category = 'Competencias') {
  const skills = measurements.filter((m) => m.category_name === category)
  const byId = new Map(skills.map((m) => [m.user_test_id, m]))
  const roots = skills.filter((m) => !m.is_follow_up).sort((a, b) => a.user_test_id - b.user_test_id)
  const parentId = (measurement: SkillsMeasurement): number | null => {
    let current: SkillsMeasurement | undefined = measurement
    const visited = new Set<number>()
    while (current?.is_follow_up) {
      if (visited.has(current.user_test_id)) return null
      visited.add(current.user_test_id)
      current = byId.get(current.root_user_test_id || current.previous_user_test_id || -1)
      if (!current || current.test_id !== measurement.test_id || current.process_id !== measurement.process_id)
        return null
    }
    return current?.user_test_id ?? null
  }
  const followUps = skills.filter((m) => m.is_follow_up).sort((a, b) => a.user_test_id - b.user_test_id)
  return {
    groups: roots.map((original) => ({
      original,
      followUps: followUps.filter((m) => parentId(m) === original.user_test_id),
    })),
    unlinked: followUps.filter((m) => parentId(m) === null),
  }
}

const statusLabel = (measurement: SkillsMeasurement) => {
  if (!measurement.completed_test) return 'Pendiente'
  const date = formatMeasurementDate(measurement.answer_date)
  return date ? `Completada · Respuesta: ${date}` : 'Completada'
}

export default function SkillsMeasurementHistory({
  measurements,
  userId,
  userName,
  companyId,
}: {
  measurements: SkillsMeasurement[]
  userId: number
  userName: string
  companyId: number
}) {
  const { groups, unlinked } = skillsMeasurementGroups(measurements)
  const role = useSelector((state: any) => state.auth.role)
  const allowed = canViewFollowUpComparison(role)
  const [selected, setSelected] = useState<{ original: SkillsMeasurement; followUp: SkillsMeasurement } | null>(null)
  const card = (measurement: SkillsMeasurement) => (
    <>
      <MeasurementMeta>
        {statusLabel(measurement)}
      </MeasurementMeta>
      <CardTypeTest
        link="/user-test-detail-skills/"
        params={{
          userId,
          userName,
          companyId,
          testId: measurement.test_id,
          processId: measurement.process_id,
          userTestId: measurement.user_test_id,
          completed: measurement.completed_test,
          language: measurement.language,
        }}
        title={measurement.is_follow_up ? '3D Skills - Follow Up' : '3D Skills'}
        image={skillIcon}
        disabled={!measurement.completed_test}
      />
    </>
  )
  return (
    <>
      {groups.map(({ original, followUps }) => (
        <BetesaGroup
          key={original.user_test_id}
          role="group"
          aria-label={`3D Skills · ${formatMeasurementLabel({ answerDate: original.answer_date })}`}
        >
          <BetesaMeasurementCard>
            <BetesaMeasurementLabel>Medición original · 3D Skills</BetesaMeasurementLabel>
            {card(original)}
          </BetesaMeasurementCard>
          {followUps.length > 0 && (
            <BetesaFollowUpList>
              {followUps.map((followUp, index) => (
                <BetesaMeasurementCard
                  key={followUp.user_test_id}
                  $isFollowUp
                  role="group"
                  aria-label={`3D Skills · ${formatMeasurementLabel({ isFollowUp: true, followUpOrdinal: index + 1, answerDate: followUp.answer_date })}`}
                >
                  <BetesaMeasurementLabel $isFollowUp>
                    Follow Up {index + 1} de 3D Skills
                  </BetesaMeasurementLabel>
                  {card(followUp)}
                  {allowed && (
                    <StrokedButton
                      disabled={!original.completed_test || !followUp.completed_test}
                      onClick={() => setSelected({ original, followUp })}
                    >
                      Comparar con original
                    </StrokedButton>
                  )}
                </BetesaMeasurementCard>
              ))}
            </BetesaFollowUpList>
          )}
        </BetesaGroup>
      ))}
      {unlinked.map((measurement) => (
        <BetesaMeasurementCard key={measurement.user_test_id} $isFollowUp>
          <BetesaMeasurementLabel $isFollowUp>
            Follow Up de 3D Skills · Referencia original no disponible
          </BetesaMeasurementLabel>
          {card(measurement)}
        </BetesaMeasurementCard>
      ))}
      {allowed && selected && (
        <SkillsComparisonDialog
          key={`${selected.followUp.process_id}-${selected.followUp.user_test_id}`}
          processId={selected.followUp.process_id}
          originalId={selected.original.user_test_id}
          followUpId={selected.followUp.user_test_id}
          userId={userId}
          userName={userName}
          onClose={() => setSelected(null)}
        />
      )}
    </>
  )
}
